/**
 * Background chats often soft-park (`stream-ended` status=parked) without a named
 * HITL/plan frame on the wire. The card lives on the session document — peek and
 * arm sidebar yellow so Needs input shows without opening the chat.
 */
import { flowopsFetch } from "@/shared/builder-session/flowops-api-url";
import { setChatLive } from "@/features/chat/multi/history/chat-history";
import { isChatAwaitingInput, setChatAwaitingInput } from "./chat-signals";

const peekInFlight = new Set<string>();
/** Sessions that currently show HITL/review in the focused builder — arm yellow on leave. */
const focusedHitlSessions = new Set<string>();

function sessionNeedsSidebarInput(snapshot: {
  status?: string;
  parkedForInput?: boolean;
  pendingUserQuestion?: unknown;
  pendingComposioExecApprovals?: unknown[];
  pendingComposioApprovals?: unknown[];
  pendingRuntimeInputRequests?: unknown;
  pauseReason?: { fields?: unknown[] };
  nodes?: unknown[];
  plan?: unknown;
}): boolean {
  if (snapshot.parkedForInput) return true;
  if (snapshot.pendingUserQuestion) return true;
  const composio = snapshot.pendingComposioExecApprovals ?? snapshot.pendingComposioApprovals;
  if (Array.isArray(composio) && composio.length > 0) return true;
  if (
    Array.isArray(snapshot.pendingRuntimeInputRequests) &&
    snapshot.pendingRuntimeInputRequests.length > 0
  ) {
    return true;
  }
  if (snapshot.pauseReason?.fields?.length) return true;

  const status = String(snapshot.status ?? "");
  if (status === "awaiting_intent_approval") return true;
  if (status === "paused") return true;
  // Plan review only — after Build, status can linger as awaiting_approval with nodes.
  if (status === "awaiting_approval") {
    const hasNodes = Array.isArray(snapshot.nodes) && snapshot.nodes.length > 0;
    return !hasNodes && Boolean(snapshot.plan);
  }
  return false;
}

/** Focused builder reports open HITL/review — used when switching away. */
export function noteFocusedSessionHitl(sessionId: string, awaiting: boolean): void {
  const id = sessionId.trim();
  if (!id) return;
  if (awaiting) focusedHitlSessions.add(id);
  else focusedHitlSessions.delete(id);
}

/** Leaving a chat that had a card — keep sidebar yellow without waiting for soft-park peek. */
export function flushLeaveSessionArm(sessionId: string): void {
  const id = sessionId.trim();
  if (!id || !focusedHitlSessions.has(id)) return;
  focusedHitlSessions.delete(id);
  setChatAwaitingInput(id, true);
  setChatLive(id, false);
}

/** Fire-and-forget; coalesces overlapping peeks per session. */
export function armSidebarAwaitingFromSession(sessionId: string): void {
  const id = sessionId.trim();
  if (!id || isChatAwaitingInput(id) || peekInFlight.has(id)) return;
  peekInFlight.add(id);
  void (async () => {
    try {
      const response = await flowopsFetch(`/api/workflow/session/${encodeURIComponent(id)}`);
      if (!response.ok) return;
      if (isChatAwaitingInput(id)) return;
      const snapshot = (await response.json()) as Parameters<typeof sessionNeedsSidebarInput>[0];
      if (!sessionNeedsSidebarInput(snapshot)) return;
      setChatAwaitingInput(id, true);
      setChatLive(id, false);
    } catch {
      // Best-effort sidebar catch-up.
    } finally {
      peekInFlight.delete(id);
    }
  })();
}

/** Test helper. */
export function resetSidebarAwaitingPeekForTests(): void {
  peekInFlight.clear();
  focusedHitlSessions.clear();
}
